import React, { useEffect, useState, useContext } from "react";
import { Grid, Typography, IconButton } from "@material-ui/core";
import {
  NotificationsActiveRounded,
  NotificationsOffRounded,
} from "@material-ui/icons";
import lodash from "lodash";
import firebase from "../firebase/index";
import AnalogList from "../components/SensorBoard/AnalogList";
import { firebaseFetchOneFactory } from "../firebase/factory";
import {
  firebaseMonitorOneSensorDisplay,
  firebaseMonitorOneSensorStatus,
} from "../firebase/sensor";
import LoadingState from "../components/Custom/LoadingState";
import { firebaseFetchOneDealer } from "../firebase/dealer";
import DigitalStatus from "../components/SensorBoard/DigitalStatus";
import DigitalAlarm from "../components/SensorBoard/DigitalAlarm";
import FullLayout from "../components/Layout/FullLayout";
import { AuthStoreContext } from "../contexts";
import {
  firebaseUpdateUserNotification,
  firebaseFetchUserProfile,
} from "../firebase/user";

export default function SensorBoardDetail(props) {
  /**
  |--------------------------------------------------
  | Props
  |--------------------------------------------------
  */
  const { dealerID, factoryID, sensorID } = props;

  /**
  |--------------------------------------------------
  | Contexts
  |--------------------------------------------------
  */
  const authStore = useContext(AuthStoreContext);

  /**
  |--------------------------------------------------
  | States
  |--------------------------------------------------
  */
  const [errorCode, setErrorCode] = useState(null);
  const [dealer, setDealer] = useState(null);
  const [factory, setFactory] = useState(null);
  const [sensor, setSensor] = useState(null);
  const [sensorDisplay, setSensorDisplay] = useState(null);
  const [sensorStatus, setSensorStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
  |--------------------------------------------------
  | Fetch Sensor
  |--------------------------------------------------
  */
  useEffect(() => {
    async function fetchSensor() {
      try {
        const dealer = await firebaseFetchOneDealer(dealerID);

        if (!dealer) {
          setErrorCode(404);
          return;
        }

        setDealer(dealer);

        const factory = await firebaseFetchOneFactory(factoryID);

        if (!factory || !factory.devices) {
          setErrorCode(404);
          return;
        }

        setFactory(factory);

        const devices = lodash.map(factory.devices, (d, key) => ({
          ...d,
          id: key,
        }));
        const sensor = lodash.find(devices, { id: sensorID });

        if (!sensor) {
          setErrorCode(404);
          return;
        }
        lodash.map(sensor.status.analog, (e) => {
          if (!e.gauge_type) {
            e.gauge_type = "nogauge";
          }
        });
        setSensor(sensor);

        firebaseMonitorOneSensorDisplay(
          factoryID,
          sensorID,
          (sensorDisplay) => {
            setSensorDisplay(sensorDisplay);
            setLoading(false);
          }
        );

        firebaseMonitorOneSensorStatus(factoryID, sensorID, (sensorStatus) => {
          setSensorStatus(sensorStatus);
          setLoading(false);
        });
      } catch (error) {
        console.error(error);
      }
    }

    fetchSensor();
    firebase
      .database()
      .ref(`mekh/${factoryID}/devices/${sensorID}`)
      .on("value", function (snapshot) {
        fetchSensor();
      });
  }, [dealerID, factoryID, sensorID]);

  /**
  |--------------------------------------------------
  | Handlers
  |--------------------------------------------------
  */
  function getDigitalStatuses() {
    const digitals = lodash.get(sensorStatus, "status.digital", []);
    const alarms = lodash.map(digitals, (digital, channel) => ({
      ...digital,
      channel,
    }));
    return lodash.filter(alarms, (digital) => digital.type.includes("status_"));
  }

  function getDigitalAlarms() {
    const digitals = lodash.get(sensorStatus, "status.digital", []);
    const alarms = lodash.map(digitals, (digital, channel) => ({
      ...digital,
      channel,
    }));
    return lodash.filter(alarms, (digital) => digital.type.includes("alarm_"));
  }

  function getStatusModbus() {
    const digitals = lodash.get(sensorStatus, "status.modbus_d", []);
    const alarms = lodash.map(digitals, (digital, channel) => ({
      ...digital,
      channel,
    }));
    return lodash.filter(alarms, (digital) => digital.type.includes("status_"));
  }

  function getAlarmModbus() {
    const digitals = lodash.get(sensorStatus, "status.modbus_d", []);
    const alarms = lodash.map(digitals, (digital, channel) => ({
      ...digital,
      channel,
    }));
    return lodash.filter(alarms, (digital) => digital.type.includes("alarm_"));
  }

  async function onUpdateSub() {
    const userProfile = await firebaseFetchUserProfile(authStore.authUser.uid);
    authStore.setAuthUserProfile(userProfile);
  }

  function handleAllSubscribe(status) {
    const analogs = lodash.get(sensor, `status.analog`, []);
    const modbuses = lodash.get(sensor, `status.modbus`, []);

    let newNotification = lodash.cloneDeep(
      lodash.get(authStore, "authUserProfile.notification", {})
    );

    // ANALOG ────────────────────────────────────────────────────────────────────────────────
    lodash.map(analogs, (analog, key) => {
      const channelNumber = key.replace("ch", "");
      lodash.set(
        newNotification,
        `topic.${sensor.id}.ai.${channelNumber}`,
        status
      );
      lodash.set(
        newNotification,
        `topic.${sensor.id}.registering.${factoryID}_${sensor.id}_ai_${channelNumber}.subscribe`,
        status
      );
    });

    // ANALOG MODBUS ────────────────────────────────────────────────────────────────────────────────
    lodash.map(modbuses, (modbus, key) => {
      const channel = key.replace("ch", "");
      lodash.set(newNotification, `topic.${sensor.id}.ma.${channel}`, status);
      lodash.set(
        newNotification,
        `topic.${sensor.id}.registering.${factoryID}_${sensor.id}_ma_${channel}.subscribe`,
        status
      );
    });

    // DIGITAL ────────────────────────────────────────────────────────────────────────────────
    const statuses = getDigitalStatuses();
    lodash.map(statuses, (diStatus) => {
      const channelNumber = diStatus.channel.replace("ch", "");
      lodash.set(
        newNotification,
        `topic.${sensor.id}.di.${channelNumber}`,
        status
      );
      lodash.set(
        newNotification,
        `topic.${sensor.id}.registering.${factoryID}_${sensor.id}_di_${channelNumber}.subscribe`,
        status
      );
    });

    // DIGITAL MODBUS ────────────────────────────────────────────────────────────────────────────────
    const diModbus = getStatusModbus();
    lodash.map(diModbus, (modbus) => {
      const channelNumber = modbus.channel.replace("ch", "");
      lodash.set(
        newNotification,
        `topic.${sensor.id}.md.${channelNumber}`,
        status
      );
      lodash.set(
        newNotification,
        `topic.${sensor.id}.registering.${factoryID}_${sensor.id}_md_${channelNumber}.subscribe`,
        status
      );
    });

    // DIGITAL ALARM ────────────────────────────────────────────────────────────────────────────────
    const alarms = getDigitalAlarms();
    lodash.map(alarms, (alarm) => {
      const channelNumber = alarm.channel.replace("ch", "");
      lodash.set(
        newNotification,
        `topic.${sensor.id}.di.${channelNumber}`,
        status
      );
      lodash.set(
        newNotification,
        `topic.${sensor.id}.registering.${factoryID}_${sensor.id}_di_${channelNumber}.subscribe`,
        status
      );
    });

    // DIGITAL ALARM MODBUS ────────────────────────────────────────────────────────────────────────────────
    const alModbus = getAlarmModbus();
    lodash.map(alModbus, (modbus) => {
      const channelNumber = modbus.channel.replace("ch", "");
      lodash.set(
        newNotification,
        `topic.${sensor.id}.md.${channelNumber}`,
        status
      );
      lodash.set(
        newNotification,
        `topic.${sensor.id}.registering.${factoryID}_${sensor.id}_md_${channelNumber}.subscribe`,
        status
      );
    });

    firebaseUpdateUserNotification(authStore.authUser.uid, newNotification);
    onUpdateSub();
  }

  /**
  |--------------------------------------------------
  | Render
  |--------------------------------------------------
  */
  if (errorCode) {
    return (
      <FullLayout
        errorCode={errorCode}
        dealerID={dealerID}
        factoryID={factoryID}
      />
    );
  }

  if (loading) {
    return (
      <FullLayout dealerID={dealerID} factoryID={factoryID}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <LoadingState />
          </Grid>
        </Grid>
      </FullLayout>
    );
  }

  /**
  |--------------------------------------------------
  | Breadcrumbs
  |--------------------------------------------------
  */
  const breadcrumbs = [
    {
      to: "/",
      label: "Dashboard",
    },
    { to: `/${dealerID}`, label: dealer.name },
    { to: `/${dealerID}/${factoryID}`, label: factory.factory.name },
    { to: null, label: sensor.name },
  ];

  return (
    <FullLayout
      breadcrumbs={breadcrumbs}
      dealerID={dealerID}
      factoryID={factoryID}
    >
      {loading ? (
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <LoadingState />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2} justify="center">
          <Grid item lg={12} sm={12}>
            <div className="flex justify-end ">
              <div
                className="flex"
                style={{
                  borderRadius: "30px",
                  borderColor: "#5C5C5C",
                  borderWidth: "1px",
                  width: "350px",
                  justifyContent: "center",
                }}
              >
                <div className="flex items-center ">
                  <div className="mr-1 text-xs">
                    <Typography variant="body2">Subscribe all:</Typography>
                  </div>
                  <IconButton onClick={() => handleAllSubscribe(true)}>
                    <NotificationsActiveRounded fontSize="large" />
                  </IconButton>
                </div>
                <div className="flex items-center">
                  <div className="mr-1 text-xs">
                    <Typography variant="body2">Unsubscribe all: </Typography>
                  </div>
                  <IconButton onClick={() => handleAllSubscribe(false)}>
                    <NotificationsOffRounded fontSize="large" />
                  </IconButton>
                </div>
              </div>
            </div>
          </Grid>
          <Grid item xs={12}>
            <p className="text-2xl " style={{ color: "#C4C4C4" }}>
              Analog sensor
            </p>
          </Grid>
          <Grid item xs={12}>
            <AnalogList
              dealerID={dealerID}
              factoryID={factoryID}
              sensor={sensor}
              sensorDisplay={sensorDisplay || {}}
            />
          </Grid>
          {/* <Grid item xs={12}>
            <Box height="300px" bgcolor="grey.800"></Box>
          </Grid> */}
          <Grid item xs={12} container>
            <Grid container spacing={3}>
              <DigitalStatus
                statuses={getDigitalStatuses()}
                modbus={getStatusModbus()}
                display={sensorDisplay}
                sensor={sensor}
                factoryID={factoryID}
              />
              <DigitalAlarm
                alarms={getDigitalAlarms()}
                modbus={getAlarmModbus()}
                display={sensorDisplay}
                sensor={sensor}
                factoryID={factoryID}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </FullLayout>
  );
}
